import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';

const V = {
  bg: '#f5f5f7',
  bgCard: '#ffffff',
  bgHover: '#eeeef0',
  border: '#e5e5ea',
  borderStrong: '#d8d8dc',
  text: '#1d1d1f',
  textDim: '#6e6e73',
  textFaint: '#a1a1a6',
  accent: '#5856d6',
  green: '#34c759',
  red: '#ff3b30',
  yellow: '#ff9500',
  blue: '#007aff',
  fontUi: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
};

const STATUS_COLORS = {
  pending: V.textFaint,
  dispatched: V.yellow,
  done: V.green,
  error: V.red,
};

const STATUS_LABELS = {
  pending: 'Pending',
  dispatched: 'Running',
  done: 'Done',
  error: 'Error',
};

const selectStyle = {
  display: 'block', width: '100%', marginTop: 4, padding: '8px 12px',
  border: `1px solid ${V.border}`, borderRadius: 8, fontSize: 14,
  fontFamily: V.fontUi, boxSizing: 'border-box', background: V.bgCard,
};

function _compareKey(idA, idB) {
  return `bench_compare:${[idA, idB].sort().join(':')}`;
}

function CompareView({ batchA, batchB, onBack }) {
  const cacheKey = _compareKey(batchA?.batch_id, batchB?.batch_id);
  const cached = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(cacheKey)); } catch { return null; }
  }, [cacheKey]);

  const [llmResult, setLlmResult] = useState(cached);
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmError, setLlmError] = useState(null);
  const [expandedCase, setExpandedCase] = useState(null);

  const casesA = batchA?.cases || [];
  const casesB = batchB?.cases || [];

  const caseMap = new Map();
  casesA.forEach(c => caseMap.set(c.case_id, { a: c }));
  casesB.forEach(c => {
    const entry = caseMap.get(c.case_id) || {};
    entry.b = c;
    caseMap.set(c.case_id, entry);
  });

  const doneA = casesA.filter(c => c.status === 'done').length;
  const doneB = casesB.filter(c => c.status === 'done').length;
  const errA = casesA.filter(c => c.status === 'error').length;
  const errB = casesB.filter(c => c.status === 'error').length;

  const runLlmCompare = async () => {
    setLlmLoading(true);
    setLlmError(null);
    try {
      const res = await api.compareBenchmarkBatches(batchA.batch_id, batchB.batch_id);
      const comparison = res.comparison;
      setLlmResult(comparison);
      try { localStorage.setItem(cacheKey, JSON.stringify(comparison)); } catch (_e) { /* quota */ }
    } catch (e) {
      setLlmError(e.message || 'LLM comparison failed');
    } finally {
      setLlmLoading(false);
    }
  };

  const winnerColors = { A: V.accent, B: V.blue, tie: V.textDim };

  return (
    <div style={{ padding: 32, fontFamily: V.fontUi, maxWidth: 900, margin: '0 auto', height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
      <button onClick={onBack} style={{
        padding: '6px 14px', background: 'none', border: `1px solid ${V.border}`,
        borderRadius: 6, fontSize: 13, cursor: 'pointer', fontFamily: V.fontUi, marginBottom: 20,
      }}>
        &larr; Back
      </button>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: V.text, marginBottom: 8 }}>
        Compare Batches
      </h2>

      {/* Summary stats */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24,
      }}>
        {[
          { label: 'A', batch: batchA, done: doneA, err: errA, color: V.accent },
          { label: 'B', batch: batchB, done: doneB, err: errB, color: V.blue },
        ].map(({ label, batch, done, err, color }) => (
          <div key={label} style={{
            padding: 16, background: V.bgCard, border: `2px solid ${color}`,
            borderRadius: 10,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', marginBottom: 6 }}>
              Batch {label}
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: V.text }}>{batch?.batch_id}</div>
            <div style={{ fontSize: 12, color: V.textDim, marginTop: 4 }}>
              tag: {batch?.tag} &middot; {done}/{batch?.total} done
              {err > 0 && <span style={{ color: V.red }}> &middot; {err} error</span>}
            </div>
            {batch?.started_at && (
              <div style={{ fontSize: 11, color: V.textFaint, marginTop: 4 }}>
                {new Date(batch.started_at).toLocaleString()}
                {batch.finished_at && (() => {
                  const sec = Math.round((new Date(batch.finished_at) - new Date(batch.started_at)) / 1000);
                  return <span> &middot; {sec < 60 ? `${sec}s` : `${Math.floor(sec/60)}m${sec%60}s`}</span>;
                })()}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* LLM evaluation */}
      <div style={{
        padding: 16, background: V.bgCard, border: `1px solid ${V.border}`,
        borderRadius: 10, marginBottom: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: llmResult ? 12 : 0 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: V.text, margin: 0 }}>
            AI Evaluation
          </h3>
          <button
            onClick={runLlmCompare}
            disabled={llmLoading}
            style={{
              padding: '6px 16px', background: llmLoading ? V.border : V.accent,
              color: '#fff', border: 'none', borderRadius: 6, fontSize: 13,
              fontWeight: 600, cursor: llmLoading ? 'wait' : 'pointer', fontFamily: V.fontUi,
            }}
          >
            {llmLoading ? 'Evaluating...' : llmResult ? 'Re-evaluate' : 'Run AI Evaluation'}
          </button>
        </div>
        {llmError && (
          <div style={{ fontSize: 12, color: V.red, marginTop: 8 }}>{llmError}</div>
        )}
        {llmResult && (
          <div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
              padding: '10px 14px', background: V.bg, borderRadius: 8,
            }}>
              <span style={{
                fontSize: 20, fontWeight: 700,
                color: winnerColors[llmResult.winner] || V.text,
              }}>
                {llmResult.winner === 'tie' ? 'Tie' : `Winner: ${llmResult.winner}`}
              </span>
              {llmResult.stats && (
                <span style={{ fontSize: 12, color: V.textDim }}>
                  A: {llmResult.stats.a_avg?.toFixed(1)} avg &middot;
                  B: {llmResult.stats.b_avg?.toFixed(1)} avg &middot;
                  {llmResult.stats.a_wins}W/{llmResult.stats.b_wins}W/{llmResult.stats.ties}T
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: V.textDim, marginBottom: 12 }}>
              {llmResult.summary}
            </div>
            {llmResult.cases?.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {llmResult.cases.map((c, i) => {
                  const key = c.case_id || i;
                  const isOpen = expandedCase === key;
                  return (
                    <div key={key}>
                      <div
                        onClick={() => setExpandedCase(isOpen ? null : key)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '6px 10px', background: V.bg, borderRadius: isOpen ? '6px 6px 0 0' : 6,
                          fontSize: 12, cursor: 'pointer',
                        }}
                      >
                        <span style={{
                          fontWeight: 700, minWidth: 20,
                          color: winnerColors[c.verdict] || V.textFaint,
                        }}>
                          {c.verdict}
                        </span>
                        <span style={{ color: V.accent, minWidth: 24 }}>{c.a_score}</span>
                        <span style={{ color: V.textFaint }}>vs</span>
                        <span style={{ color: V.blue, minWidth: 24 }}>{c.b_score}</span>
                        <span style={{
                          flex: 1, color: V.textDim,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {c.prompt_preview || c.case_id}
                        </span>
                        <span style={{ color: V.textFaint, fontSize: 11, flexShrink: 0 }}>
                          {isOpen ? '▲' : '▼'}
                        </span>
                      </div>
                      {isOpen && (
                        <div style={{
                          padding: '8px 12px', background: '#f8f8fa', borderRadius: '0 0 6px 6px',
                          fontSize: 12, color: V.textDim, lineHeight: 1.5, borderTop: `1px solid ${V.border}`,
                        }}>
                          {c.reason}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Per-case comparison */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: V.text, marginBottom: 12 }}>
        Case-by-Case ({caseMap.size} cases)
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[...caseMap.entries()].map(([caseId, { a, b }], i) => {
          const prompt = a?.prompt_preview || b?.prompt_preview || caseId;
          const statusChanged = (a?.status || 'missing') !== (b?.status || 'missing');
          const llmCase = llmResult?.cases?.find(lc => lc.case_id === caseId);
          return (
            <div key={caseId} style={{
              padding: '12px 16px', background: V.bgCard,
              border: `1px solid ${statusChanged ? V.yellow : V.border}`, borderRadius: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ color: V.textFaint, fontSize: 13, minWidth: 24 }}>#{i + 1}</span>
                <span style={{
                  fontSize: 14, color: V.text, flex: 1,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {prompt}
                </span>
                {llmCase && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                    background: llmCase.verdict === 'tie' ? '#f0f0f0' : llmCase.verdict === 'A' ? '#f0eeff' : '#eef4ff',
                    color: winnerColors[llmCase.verdict] || V.textFaint,
                  }}>
                    {llmCase.verdict === 'tie' ? 'Tie' : `${llmCase.verdict} wins`} ({llmCase.a_score} vs {llmCase.b_score})
                  </span>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { label: 'A', c: a, color: V.accent },
                  { label: 'B', c: b, color: V.blue },
                ].map(({ label, c, color }) => (
                  <div key={label} style={{
                    padding: '8px 12px', background: V.bg, borderRadius: 8,
                    borderLeft: `3px solid ${color}`,
                  }}>
                    {c ? (
                      <>
                        <span style={{
                          display: 'inline-block', padding: '1px 6px', borderRadius: 4,
                          fontSize: 11, fontWeight: 600, marginBottom: 4,
                          background: c.status === 'done' ? '#e8f9ed' : c.status === 'error' ? '#ffe5e5' : '#f0f0f0',
                          color: STATUS_COLORS[c.status] || V.textFaint,
                        }}>
                          {STATUS_LABELS[c.status] || c.status}
                        </span>
                        {c.error && (
                          <div style={{ fontSize: 11, color: V.red, marginTop: 2 }}>{c.error}</div>
                        )}
                        {c.share_url && (
                          <a
                            href={`/playground${c.share_url}`}
                            target="_blank" rel="noreferrer"
                            style={{ fontSize: 11, color: V.accent, display: 'block', marginTop: 4 }}
                          >
                            View result &rarr;
                          </a>
                        )}
                      </>
                    ) : (
                      <span style={{ fontSize: 12, color: V.textFaint }}>Not in this batch</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function BatchProgressPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const [startForm, setStartForm] = useState({ tag: '', concurrency: 3, batchId: '' });
  const [starting, setStarting] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [history, setHistory] = useState([]);

  // Compare mode
  const [compareSelection, setCompareSelection] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    if (!batchId) {
      api.listBenchmarkCases().then(res => {
        const cases = res.cases || res || [];
        const tagSet = new Set();
        cases.forEach(c => (c.tags || []).forEach(t => tagSet.add(t)));
        const sorted = [...tagSet].sort();
        setAvailableTags(sorted);
        if (sorted.length && !startForm.tag) {
          setStartForm(f => ({ ...f, tag: sorted[0] }));
        }
      }).catch(() => {});

      // Load from server
      api.listBenchmarkBatches().then(res => {
        setHistory(res.batches || []);
      }).catch(() => {
        setHistory([]);
      });
    }
  }, [batchId]);

  const poll = useCallback(async (id) => {
    try {
      const data = await api.getBenchmarkBatch(id);
      setBatch(data);
      setError(null);
      if (data.status !== 'done') {
        timerRef.current = setTimeout(() => poll(id), 5000);
      }
    } catch (e) {
      setError(e.message || 'Failed to fetch batch');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (batchId) {
      setLoading(true);
      poll(batchId);
    } else {
      setLoading(false);
    }
    return () => clearTimeout(timerRef.current);
  }, [batchId, poll]);

  const handleStart = async (e) => {
    e.preventDefault();
    if (!startForm.tag.trim()) return;
    setStarting(true);
    try {
      const data = await api.startBenchmarkBatch({
        tag: startForm.tag.trim(),
        concurrency: startForm.concurrency,
        batchId: startForm.batchId.trim() || undefined,
      });
      navigate(`/playground/batch/${data.batch_id}`);
    } catch (err) {
      setError(err.message || 'Failed to start batch');
    } finally {
      setStarting(false);
    }
  };

  const toggleCompareSelect = (batchId) => {
    setCompareSelection(prev => {
      if (prev.includes(batchId)) return prev.filter(id => id !== batchId);
      if (prev.length >= 2) return [prev[1], batchId];
      return [...prev, batchId];
    });
  };

  const handleCompare = async () => {
    if (compareSelection.length !== 2) return;
    setComparing(true);
    try {
      const [a, b] = await Promise.all(
        compareSelection.map(id => api.getBenchmarkBatch(id))
      );
      setCompareData({ a, b });
    } catch (err) {
      setError(err.message || 'Failed to load batches for comparison');
    } finally {
      setComparing(false);
    }
  };

  if (compareData) {
    return (
      <CompareView
        batchA={compareData.a}
        batchB={compareData.b}
        onBack={() => { setCompareData(null); setCompareSelection([]); }}
      />
    );
  }

  const completedCount = batch ? batch.cases.filter(c => c.status === 'done' || c.status === 'error').length : 0;
  const doneCount = batch ? batch.cases.filter(c => c.status === 'done').length : 0;
  const errorCount = batch ? batch.cases.filter(c => c.status === 'error').length : 0;
  const progress = batch ? (completedCount / batch.total) * 100 : 0;

  if (!batchId) {
    return (
      <div style={{ padding: 32, fontFamily: V.fontUi, maxWidth: 520, margin: '0 auto', height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: V.text, marginBottom: 24 }}>
          Start Benchmark Batch
        </h2>
        <form onSubmit={handleStart} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label style={{ color: V.textDim, fontSize: 13 }}>
            Tag *
            <select
              value={startForm.tag}
              onChange={e => setStartForm(f => ({ ...f, tag: e.target.value }))}
              style={selectStyle}
            >
              {availableTags.length === 0 && <option value="">Loading tags...</option>}
              {availableTags.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
          <label style={{ color: V.textDim, fontSize: 13 }}>
            Concurrency (1-10)
            <input
              type="number" min={1} max={10}
              value={startForm.concurrency}
              onChange={e => setStartForm(f => ({ ...f, concurrency: parseInt(e.target.value) || 3 }))}
              style={selectStyle}
            />
          </label>
          <label style={{ color: V.textDim, fontSize: 13 }}>
            Batch ID (optional, auto-generated if empty)
            <input
              type="text"
              value={startForm.batchId}
              onChange={e => setStartForm(f => ({ ...f, batchId: e.target.value }))}
              placeholder="e.g. my-test-run"
              style={selectStyle}
            />
          </label>
          {error && <div style={{ color: V.red, fontSize: 13 }}>{error}</div>}
          <button
            type="submit"
            disabled={starting || !startForm.tag.trim()}
            style={{
              padding: '10px 20px', background: V.accent, color: '#fff', border: 'none',
              borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer',
              opacity: starting ? 0.6 : 1, fontFamily: V.fontUi,
            }}
          >
            {starting ? 'Starting...' : 'Start Batch'}
          </button>
        </form>

        {/* Compare bar */}
        {compareSelection.length > 0 && (
          <div style={{
            position: 'sticky', bottom: 0, padding: '12px 0', marginTop: 16,
            background: V.bg, borderTop: `1px solid ${V.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 13, color: V.textDim }}>
              {compareSelection.length}/2 selected for compare
            </span>
            <button
              onClick={handleCompare}
              disabled={compareSelection.length !== 2 || comparing}
              style={{
                padding: '8px 18px', background: compareSelection.length === 2 ? V.blue : V.border,
                color: '#fff', border: 'none', borderRadius: 8, fontSize: 13,
                fontWeight: 600, cursor: compareSelection.length === 2 ? 'pointer' : 'default',
                fontFamily: V.fontUi,
              }}
            >
              {comparing ? 'Loading...' : 'Compare'}
            </button>
          </div>
        )}

        {history.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: V.text, margin: 0 }}>
                All Batches
              </h3>
              <span style={{ fontSize: 12, color: V.textFaint }}>
                Click checkbox to compare
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {history.map(h => {
                const selected = compareSelection.includes(h.batch_id);
                return (
                  <div
                    key={h.batch_id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', background: selected ? '#f0f0ff' : V.bgCard,
                      border: `1px solid ${selected ? V.accent : V.border}`, borderRadius: 8,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleCompareSelect(h.batch_id)}
                      style={{ cursor: 'pointer', accentColor: V.accent }}
                    />
                    <Link
                      to={`/playground/batch/${h.batch_id}`}
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        textDecoration: 'none', color: V.text, fontSize: 13,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>{h.batch_id}</div>
                        <div style={{ color: V.textFaint, fontSize: 12, marginTop: 2 }}>
                          tag: {h.tag} &middot; {h.started_at ? new Date(h.started_at).toLocaleString() : ''}
                          {h.started_at && h.finished_at && (() => {
                            const sec = Math.round((new Date(h.finished_at) - new Date(h.started_at)) / 1000);
                            return <span> &middot; {sec < 60 ? `${sec}s` : `${Math.floor(sec/60)}m${sec%60}s`}</span>;
                          })()}
                        </div>
                      </div>
                      <span style={{
                        padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                        background: h.status === 'done' ? '#e8f9ed' : '#fff8e6',
                        color: h.status === 'done' ? V.green : V.yellow,
                      }}>
                        {h.status === 'done'
                          ? `${h.completed || 0}/${h.total || '?'} done${h.errors ? `, ${h.errors} err` : ''}`
                          : 'running'}
                      </span>
                    </Link>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Delete batch ${h.batch_id}?`)) return;
                        try {
                          await api.deleteBenchmarkBatch(h.batch_id);
                          setHistory(prev => prev.filter(x => x.batch_id !== h.batch_id));
                          setCompareSelection(prev => prev.filter(x => x !== h.batch_id));
                        } catch (err) {
                          alert(`Delete failed: ${err.message}`);
                        }
                      }}
                      style={{
                        padding: '4px 8px', background: 'none', border: `1px solid ${V.border}`,
                        borderRadius: 4, fontSize: 11, color: V.red, cursor: 'pointer',
                        fontFamily: V.fontUi, flexShrink: 0,
                      }}
                      title="Delete batch"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Comparison Results from cache */}
        {(() => {
          const saved = [];
          try {
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (!key.startsWith('bench_compare:')) continue;
              const parts = key.replace('bench_compare:', '').split(':');
              if (parts.length !== 2) continue;
              const data = JSON.parse(localStorage.getItem(key));
              if (!data) continue;
              saved.push({ idA: parts[0], idB: parts[1], data, key });
            }
          } catch (_e) { /* ignore */ }
          if (!saved.length) return null;
          return (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: V.text, marginBottom: 12 }}>
                Comparison Results
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {saved.map(s => {
                  const w = s.data.winner;
                  const wColor = w === 'A' ? V.accent : w === 'B' ? V.blue : V.textDim;
                  return (
                    <div
                      key={s.key}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px', background: V.bgCard,
                        border: `1px solid ${V.border}`, borderRadius: 8, cursor: 'pointer',
                      }}
                      onClick={async () => {
                        setComparing(true);
                        try {
                          const [a, b] = await Promise.all([
                            api.getBenchmarkBatch(s.idA),
                            api.getBenchmarkBatch(s.idB),
                          ]);
                          setCompareData({ a, b });
                        } catch (err) {
                          setError(err.message || 'Failed to load batches');
                        } finally {
                          setComparing(false);
                        }
                      }}
                    >
                      <span style={{
                        padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                        background: wColor + '18', color: wColor,
                      }}>
                        {w === 'tie' ? 'Tie' : `${w} wins`}
                      </span>
                      <div style={{ flex: 1, fontSize: 12 }}>
                        <span style={{ fontWeight: 600, color: V.text }}>
                          {s.idA.slice(0, 20)}
                        </span>
                        <span style={{ color: V.textFaint }}> vs </span>
                        <span style={{ fontWeight: 600, color: V.text }}>
                          {s.idB.slice(0, 20)}
                        </span>
                        <span style={{ color: V.textDim, marginLeft: 8 }}>
                          A: {(s.data.stats?.a_avg ?? s.data.avg_a)?.toFixed(1)} · B: {(s.data.stats?.b_avg ?? s.data.avg_b)?.toFixed(1)}
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!window.confirm('Delete this comparison result?')) return;
                          try { localStorage.removeItem(s.key); } catch (_e) { /* ignore */ }
                          setCompareSelection([...compareSelection]);
                        }}
                        style={{
                          padding: '4px 8px', background: 'none', border: `1px solid ${V.border}`,
                          borderRadius: 4, fontSize: 11, color: V.red, cursor: 'pointer',
                          fontFamily: V.fontUi, flexShrink: 0,
                        }}
                        title="Delete comparison"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  return (
    <div style={{ padding: 32, fontFamily: V.fontUi, maxWidth: 800, margin: '0 auto', height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: V.text, margin: 0 }}>
          Batch: {batchId}
        </h2>
        {batch && (
          <span style={{
            padding: '4px 12px', borderRadius: 12, fontSize: 13, fontWeight: 600,
            background: batch.status === 'done' ? '#e8f9ed' : '#fff8e6',
            color: batch.status === 'done' ? V.green : V.yellow,
          }}>
            {batch.status === 'done' ? 'Completed' : 'Running'}
          </span>
        )}
      </div>

      {loading && <div style={{ color: V.textDim }}>Loading...</div>}
      {error && <div style={{ color: V.red, marginBottom: 16 }}>{error}</div>}

      {batch && (
        <>
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, color: V.textDim }}>
              <span>Tag: <strong style={{ color: V.text }}>{batch.tag}</strong></span>
              <span>
                <strong style={{ color: V.green }}>{doneCount}</strong> done
                {errorCount > 0 && <>, <strong style={{ color: V.red }}>{errorCount}</strong> error</>}
                {' / '}{batch.total} total
              </span>
            </div>
            <div style={{ height: 8, background: '#e8e8ed', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 4, transition: 'width 0.5s ease',
                width: `${progress}%`,
                background: errorCount > 0
                  ? `linear-gradient(90deg, ${V.green} ${(doneCount / batch.total) * 100}%, ${V.red} 0%)`
                  : V.green,
              }} />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {batch.cases.map((c, i) => (
              <div
                key={c.case_id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 16px', background: V.bgCard,
                  border: `1px solid ${V.border}`, borderRadius: 10,
                }}
              >
                <span style={{ color: V.textFaint, fontSize: 13, minWidth: 24 }}>#{i + 1}</span>
                <span style={{
                  width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                  background: STATUS_COLORS[c.status] || V.textFaint,
                  animation: c.status === 'dispatched' ? 'pulse 1.5s infinite' : 'none',
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 14, color: V.text, whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {c.prompt_preview}
                  </div>
                  <div style={{ fontSize: 12, color: V.textFaint, marginTop: 2 }}>
                    {c.worker_id} &middot; {STATUS_LABELS[c.status] || c.status}
                    {c.error && <span style={{ color: V.red }}> &middot; {c.error}</span>}
                  </div>
                </div>
                {c.share_url && (
                  <a
                    href={`/playground${c.share_url}`}
                    target="_blank" rel="noreferrer"
                    style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 12,
                      background: V.accent, color: '#fff', textDecoration: 'none',
                      fontWeight: 600, flexShrink: 0,
                    }}
                  >
                    View
                  </a>
                )}
              </div>
            ))}
          </div>

          {batch.status !== 'done' && (
            <div style={{ textAlign: 'center', color: V.textFaint, fontSize: 13, marginTop: 16 }}>
              Auto-refreshing every 5s...
            </div>
          )}

          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.4; }
            }
          `}</style>
        </>
      )}
    </div>
  );
}
